/** The page's strings and labels.
 *
 *  They live here rather than in the view so that the words a reader is judged on sit in one
 *  file, short enough to be read end to end before changing one of them. */
import { CAP_BENCH, type Domain } from '../engine/capability'
import { pick as choose, t } from './lang'
import type {
  CatalogInputs, ItemEstimate, ModelQuote, Recommendations,
} from '../engine/catalog'

/**
 * Ce qui a manqué à ce modèle sur ce travail, ou null quand il n'y a rien à signaler — y
 * compris quand il a tout réussi : une carte ne raconte pas nos essais, elle prévient.
 */
export function automationBenchFr(domain: Domain, modelId: string): {
  text: string; warn: boolean
} | null {
  const bench = CAP_BENCH[domain]
  if (!bench) return null
  if (bench.steady.includes(modelId)) return null
  if (bench.passed.includes(modelId)) {
    return {
      text: t('Il lui est arrivé de ne pas aller au bout de ce travail.',
        'It has been known not to see this work through.'),
      warn: true,
    }
  }
  if (bench.failed.includes(modelId)) {
    return {
      text: t('N’est pas allé au bout de ce travail.', 'Did not see this work through.'),
      warn: true,
    }
  }
  return null
}

/**
 * Ce qu'est chaque modèle, en une phrase, pour un lecteur qui n'en a jamais lancé un.
 *
 * Ce ne sont PAS nos mesures : elles viennent des annonces des éditeurs et de la presse
 * tech, relevées le 21/09/2026 — les sources sont listées dans `docs/modeles.md`. La note
 * chiffrée et le prix, eux, restent au-dessus de la phrase. Un modèle sans phrase retombe
 * sur la raison du choix, et `tests/wording.test.ts` refuse qu'un modèle de la grille reste
 * sans description.
 */
export const MODEL_ABOUT_FR: Record<string, string> = {
  'anthropic/claude-fable-5.1':
    'Le modèle haut de gamme d’Anthropic, taillé pour le code et les tâches longues où '
    + 'l’agent enchaîne beaucoup d’étapes, réputé pour ne pas bâcler et chercher la cause réelle d’un problème.',
  'anthropic/claude-opus-5':
    'Le grand modèle polyvalent d’Anthropic, un cran sous le Fable, présenté pour le '
    + 'travail quotidien des entreprises, réputé bon pour vérifier son résultat et recommencer jusqu’à ce qu’il tienne.',
  'anthropic/claude-opus-4.6':
    'Le grand modèle de la génération précédente chez Anthropic, apprécié pour la '
    + 'planification, la revue de code et le débogage dans de gros projets.',
  'anthropic/claude-sonnet-5':
    'Le modèle intermédiaire d’Anthropic, pensé comme une façon moins chère de faire '
    + 'tourner des agents, réputé bon pour planifier et se servir d’outils comme un navigateur ou un terminal.',
  'anthropic/claude-haiku-4.5':
    'Le petit modèle rapide d’Anthropic, prévu pour les usages à gros volume où la vitesse '
    + 'et le coût priment, réputé correct en code malgré sa taille.',
  'openai/gpt-6-astra':
    'Le modèle le plus récent d’OpenAI, réputé bon pour se servir d’un ordinateur et d’un '
    + 'navigateur, et pour mener des tâches professionnelles en plusieurs étapes.',
  'openai/gpt-5.6-terra':
    'Le modèle intermédiaire de la série GPT-5.6 d’OpenAI, positionné comme l’équilibre '
    + 'entre capacité et coût pour un usage courant en production.',
  'openai/gpt-5.6-sol':
    'Le grand modèle de la série GPT-5.6 d’OpenAI, celui qui raisonne le plus longtemps, '
    + 'réputé bon pour le code en autonomie et les problèmes qui demandent beaucoup d’étapes.',
  'openai/gpt-5.6-luna':
    'Le petit modèle rapide d’OpenAI, taillé pour les traitements à gros volume et peu '
    + 'complexes — résumer, classer, rédiger, automatiser — à une fraction du prix des grands.',
  'openai/gpt-5.5':
    'Le modèle d’OpenAI de la génération précédant les 5.6, réputé solide à la fois en '
    + 'rédaction, en code et en raisonnement, avec une variante dédiée à la cybersécurité.',
  'openai/gpt-5.4-mini':
    'Le petit modèle rapide d’OpenAI, prévu pour les traitements à gros volume, correct en '
    + 'code et en usage d’outils, et il accepte aussi les images en entrée.',
  'google/gemini-3.8-flash':
    'Le modèle rapide de Google, dernier de la gamme Flash, annoncé pour le développement '
    + 'logiciel au long cours et les agents autonomes, avec un temps de réflexion réglable.',
  'deepseek/deepseek-v4.1-flash':
    'Le modèle ouvert et économe de DeepSeek, le plus petit de sa nouvelle architecture, '
    + 'conçu pour répondre vite et à faible coût, avec la lecture d’images et une très longue mémoire.',
  'z-ai/glm-5.3':
    'Le modèle phare du chinois Z.ai, aux poids ouverts, orienté programmation et agents, '
    + 'réputé solide sur les tâches de développement longues et l’enchaînement d’outils.',
  'z-ai/glm-5.3-flash':
    'La version légère et bon marché du modèle de Z.ai, publiée en licence libre, qui '
    + 'comprend images et vidéos et vise les usages où il faut beaucoup de requêtes pour peu cher.',
  'x-ai/grok-4.6':
    'Le modèle de pointe de xAI, pensé pour les travaux qui restent ouverts sur de '
    + 'nombreuses étapes, comme explorer un dépôt de code entier ou mener une recherche de bout en bout.',
  'moonshotai/kimi-k3':
    'Le grand modèle ouvert du chinois Moonshot AI, téléchargeable librement, réputé '
    + 'proche des grands modèles fermés et capable d’avaler d’un coup une base de code entière.',
  'qwen/qwen3.8-max-0902':
    'Le grand modèle maison d’Alibaba, dans sa version 0902 retravaillée pour le code et '
    + 'les agents, également réputé bon pour lire des documents, des graphiques et des vidéos.',
  'minimax/minimax-m3':
    'Le modèle à poids ouverts de MiniMax, qui vise le niveau des modèles fermés sur le '
    + 'code et les agents, avec la lecture d’images et de vidéos et le pilotage d’un ordinateur.',
}

/** The same sentences in English. Same sources, same rule: nothing invented. */
export const MODEL_ABOUT_EN: Record<string, string> = {
  'anthropic/claude-fable-5.1':
    'Anthropic’s top-end model, built for code and for long tasks where the agent chains '
    + 'many steps, known for not cutting corners and for chasing the real cause of a problem.',
  'anthropic/claude-opus-5':
    'Anthropic’s big all-rounder, a notch below Fable, presented for everyday business work '
    + 'and known for checking its own output and starting over until it holds.',
  'anthropic/claude-opus-4.6':
    'Anthropic’s large model from the previous generation, valued for planning, code review '
    + 'and debugging inside big projects.',
  'anthropic/claude-sonnet-5':
    'Anthropic’s middle model, meant as a cheaper way to run agents, known for planning and '
    + 'for using tools such as a browser or a terminal.',
  'anthropic/claude-haiku-4.5':
    'Anthropic’s small fast model, made for high-volume uses where speed and cost come first, '
    + 'and reckoned decent at code despite its size.',
  'openai/gpt-6-astra':
    'OpenAI’s most recent model, known for driving a computer and a browser, and for seeing '
    + 'multi-step professional tasks through.',
  'openai/gpt-5.6-terra':
    'The middle model of OpenAI’s GPT-5.6 line, positioned as the balance between capability '
    + 'and cost for everyday production use.',
  'openai/gpt-5.6-sol':
    'The large model of OpenAI’s GPT-5.6 line, the one that reasons longest, known for '
    + 'autonomous coding and for problems that take many steps.',
  'openai/gpt-5.6-luna':
    'OpenAI’s small fast model, cut out for high-volume, low-complexity work — summarising, '
    + 'classifying, drafting, automating — at a fraction of the price of the big ones.',
  'openai/gpt-5.5':
    'OpenAI’s model from the generation before 5.6, reckoned solid at writing, code and '
    + 'reasoning alike, with a variant devoted to cybersecurity.',
  'openai/gpt-5.4-mini':
    'OpenAI’s small fast model, meant for high-volume processing, decent at code and at '
    + 'using tools, and it takes images as input too.',
  'google/gemini-3.8-flash':
    'Google’s fast model, the latest of the Flash line, announced for long-running software '
    + 'development and autonomous agents, with adjustable thinking time.',
  'deepseek/deepseek-v4.1-flash':
    'DeepSeek’s open, frugal model, the smallest of its new architecture, built to answer '
    + 'fast and cheap, with image reading and a very long memory.',
  'z-ai/glm-5.3':
    'The flagship model of China’s Z.ai, open-weights, aimed at programming and agents, '
    + 'reckoned solid on long development tasks and on chaining tools.',
  'z-ai/glm-5.3-flash':
    'The light, cheap version of Z.ai’s model, released under a free licence, which '
    + 'understands images and video and targets uses that need many requests for little money.',
  'x-ai/grok-4.6':
    'xAI’s frontier model, meant for work that stays open over many steps, such as exploring '
    + 'a whole code base or running a piece of research end to end.',
  'moonshotai/kimi-k3':
    'The big open model from China’s Moonshot AI, freely downloadable, reckoned close to the '
    + 'large closed models and able to swallow a whole code base at once.',
  'qwen/qwen3.8-max-0902':
    'Alibaba’s in-house large model, in its 0902 version reworked for code and agents, also '
    + 'reckoned good at reading documents, charts and video.',
  'minimax/minimax-m3':
    'MiniMax’s open-weights model, aiming at the level of the closed ones on code and agents, '
    + 'with image and video reading and computer use.',
}

/** The card column of both model tables: the card titles, shortened. A model can hold two. */
export function pickTagsFr(quote: Pick<ModelQuote, 'picks'>): string {
  const tags = {
    cheapest: t('moins cher', 'cheapest'),
    value: t('qualité / prix', 'best value'),
    best: t('meilleur', 'best'),
  } as const
  return quote.picks.map((kind) => tags[kind]).join(' · ')
}

/**
 * Le plafond, dit en une ligne au-dessus des cartes.
 *
 * Il a porté trois phrases : le niveau sur 100, le modèle que le plafond écarte et le prix
 * qu'il aurait coûté. Trop long et trop précis pour ce que le lecteur en fait (Armand,
 * 21/09/2026) — ce qu'il doit savoir tient en un fait : plus cher n'apportera rien.
 * Quand aucun modèle n'atteint le plafond, il ne change rien et la page se tait.
 */
export function capSentenceFr(
  recommendations: Pick<Recommendations, 'cap' | 'picks' | 'billing'>,
): string {
  if (!recommendations.cap?.binds) return ''
  return t('Sur ce travail, monter en gamme n’apporte rien de plus : au-dessus de ce niveau, '
    + 'les modèles font la même chose, plus cher.',
  'On this work, moving up the range adds nothing: above this level, the models do the same '
    + 'thing, for more.')
}

/** Sur les cartes, les libellés qualifient « projet » : masculins. */
export function ampleurLabels(): string[] {
  return choose(['Petit', 'Moyen', 'Large'], ['Small', 'Medium', 'Large'])
}

/** Dans une phrase, ils qualifient « taille » : féminins. Deux formes, parce que
 *  c'est du français, pas un défaut — et elles sont côte à côte pour ne pas
 *  diverger. */
export function tailleProse(): string[] {
  return choose(['petite', 'moyenne', 'large'], ['small', 'medium', 'large'])
}

/**
 * Ce que « petit », « moyen » et « large » veulent dire ici.
 *
 * Rien d'absolu : l'échelle positionne le projet ENTRE le plus petit et le plus
 * gros de ce type qu'on ait réellement mesurés. Un « large » site vitrine reste
 * plus léger qu'un « petit » produit complet, et c'est voulu — le seuil est celui
 * du corpus, pas un barème inventé.
 *
 * Le repère donné au lecteur est l'étape du produit, dans ses mots à lui : POC,
 * MVP, produit fini (Armand, 21/09/2026).
 */
export function tailleHints(): string[] {
  return choose([
  'Un POC (proof of concept) minimal, pas une version finie.',
  'Un MVP avec l’essentiel des fonctionnalités.',
  'Le projet dans son intégralité, avec un maximum de fonctionnalités.',
], [
  'A minimal proof of concept, not a finished version.',
  'An MVP with the features that matter.',
  'The whole project, with as many features as it takes.',
  ])
}

/** The owner's own word. Two judges read it as ambiguous — nothing was typed, so
 *  "search" surprises — and the recap line above the button is the mitigation.
 *  Kept as a constant so changing it is one line, not a hunt. */
export function ctaLabel(again: boolean): string {
  return again ? t('Rafraîchir les prix', 'Refresh the prices') : t('Rechercher', 'Search')
}

/** One line naming exactly what is being priced.
 *
 *  Without it the comparison floats: a reader sees "3,49 € against 278 €" with no
 *  idea which job that is. And the job matters — the ordering below the fourth
 *  place reshuffles with the mix of writing versus re-reading, so a model table
 *  is never a general ranking of models. */
export function jobDescription(estimate: ItemEstimate, current: CatalogInputs): string {
  // Seule l'initiale tombe : tout passer en minuscules écrivait « une api ou un backend ».
  const lower = (text: string) => text.charAt(0).toLowerCase() + text.slice(1)
  if (estimate.item.kind === 'automation') {
    const job = lower(estimate.typeLabel)
    return t(`une exécution de « ${job} »`, `one run of “${job}”`)
  }
  const size = tailleProse()[Math.round(current.ampleur * 2)]
    ?? t('moyenne', 'medium')
  const visual = current.modifiers['visual'] !== false
    ? t('avec vérification du rendu à l’écran', 'with on-screen checking')
    : t('sans vérification du rendu', 'without on-screen checking')
  // En anglais le libellé porte déjà son article (« A web application ») : le répéter
  // écrivait « a medium a web application ». La taille passe donc après, en apposition.
  return t(`${lower(estimate.typeLabel)} de taille ${size}, ${visual}`,
    `${estimate.typeLabel} — ${size} size, ${visual}`)
}

/** La phrase qui présente un modèle, dans la langue affichée. */
export function modelAbout(modelId: string): string | undefined {
  const fr = MODEL_ABOUT_FR[modelId]
  const en = MODEL_ABOUT_EN[modelId]
  if (fr === undefined || en === undefined) return fr ?? en
  return t(fr, en)
}
